import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Stack, Autocomplete, TextField, IconButton, Tooltip,
  FormControlLabel, Checkbox, InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import TerrainIcon from '@mui/icons-material/Terrain';
import ExploreIcon from '@mui/icons-material/Explore';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FlagIcon from '@mui/icons-material/Flag';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { cores, campoEscuro } from '../styles/styleUtils';

// Mapa ícone por campo (mesmo padrão de IconesProduto.js / tiposAtividade.js).
// Ficava num arquivo IconesCampoInstalacao.js separado — trazido pra cá
// porque os dois arquivos vinham se misturando ao salvar. Só usado neste
// componente, então não faz falta separado.
const ICONE_POR_CAMPO = {
  cliente: PersonIcon,
  estado: PublicIcon,
  cidade: LocationCityIcon,
  superficie: TerrainIcon,
  orientacao: ExploreIcon,
  disjuntor: FlashOnIcon,
  origem: FlagIcon,
  unidade: ApartmentIcon,
};

// Extraído dos 6 blocos praticamente idênticos em EditProposta.js (um por
// tipo de produto — On-Grid, Bombeamento, Off-Grid, Seguro, Limpeza,
// Serviços — mais o bloco do Personalizado, que tem a mesma estrutura
// embora não estivesse contado na tabela original do handoff). Cada bloco
// tinha um subconjunto ligeiramente diferente de campos (ex.: Bombeamento
// não tem Superfície) — por isso o componente é parametrizado pela prop
// `campos`, em vez de assumir a lista inteira.
//
// Estilo: usa campoEscuro()/cores do styleUtils.js, que já carregam o
// token de borderRadius adicionado em labelCampoEscuro — não precisa
// repetir aqui.

const styleTextField = { ...campoEscuro('30vw'), '@media screen and (max-width: 600px)': { width: '70vw' } };
const styleTextFieldPequeno = { ...campoEscuro('14vw'), '@media screen and (max-width: 600px)': { width: '34vw' } };

const IconButtonWrapper = styled('div')({
  display: 'flex', alignItems: 'center', marginBottom: 8,
});

// ID fixo da orientação "Sul/Sudoeste/Sudeste" — mesmo valor usado em todo
// o sistema (EditProposta.js, DimensionamentoOnGrid.js) para decidir se o
// checkbox de Rebatimento aparece.
const ID_ORIENTACAO_SUL_SUDOESTE_SUDESTE = '6508a00a326edddd6ff36618';

// Ícone à esquerda do campo, tanto na lista aberta quanto no campo
// fechado — mesmo padrão usado no seletor de Produto (Tópico 1).
function withIcone(chave, label) {
  const Icone = ICONE_POR_CAMPO[chave];
  return (params) => (
    <TextField
      {...params}
      label={label}
      InputProps={{
        ...params.InputProps,
        startAdornment: Icone ? (
          <InputAdornment position="start">
            <Icone fontSize="small" sx={{ color: cores.laranja }} />
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

const CamposInstalacao = ({
  campos,
  proposta,
  setProposta,
  rowsClientes,
  estados,
  cidades,
  superficies,
  orientacoes,
  tiposDisjuntor,
  potenciasDisjuntor,
  origens,
  unidades,
  isSameUnidade,
  onAbrirModalCliente,
}) => {
  const tem = (chave) => campos.includes(chave);

  return (
    <Stack alignItems="center" justifyContent="center" flex={1} gap={2}>
      {tem('cliente') && (
        <IconButtonWrapper>
          <Autocomplete
            disablePortal
            id="cliente"
            value={proposta.cliente || null}
            options={rowsClientes}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => `${String(option.codigo || '').padStart(5, '0')} - ${option.nome}`}
            renderOption={(props, option) => {
              const IconePessoa = ICONE_POR_CAMPO.cliente;
              return (
                <li {...props} key={option._id}>
                  <IconePessoa fontSize="small" sx={{ mr: 1.5, color: cores.laranja }} />
                  {option.nome} - {option.cpf}
                </li>
              );
            }}
            renderInput={withIcone('cliente', 'Cliente')}
            onChange={(event, newValue) => {
              setProposta((prevProposta) => ({ ...prevProposta, cliente: newValue }));
            }}
            sx={styleTextField}
          />
          <IconButton aria-label="add" onClick={onAbrirModalCliente}>
            {proposta.cliente ? <EditIcon /> : <AddCircleIcon />}
          </IconButton>
          {proposta.cliente && !proposta.cliente.cpf && (
            <Tooltip title="Complete o cadastro do cliente">
              <WarningAmberIcon color="warning" />
            </Tooltip>
          )}
        </IconButtonWrapper>
      )}

      {tem('estado') && (
        <Autocomplete
          disablePortal
          id="estadolocalizacao"
          value={proposta.localinstalacao?.estado || null}
          options={estados}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.nome} - ${option.sigla}`}
          sx={styleTextField}
          renderInput={withIcone('estado', 'Estado Instalação')}
          onChange={(event, newValue) => {
            setProposta((prevProposta) => ({
              ...prevProposta,
              localinstalacao: { ...prevProposta.localinstalacao, estado: newValue },
            }));
          }}
        />
      )}

      {tem('cidade') && (
        <Autocomplete
          disablePortal
          id="cidadeinstalacao"
          value={proposta.localinstalacao?.cidade || null}
          options={cidades}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.nome}`}
          sx={styleTextField}
          renderInput={withIcone('cidade', 'Cidade Instalação')}
          onChange={(event, newValue) => {
            setProposta((prevProposta) => ({
              ...prevProposta,
              localinstalacao: { ...prevProposta.localinstalacao, cidade: newValue },
            }));
          }}
        />
      )}

      {tem('superficie') && (
        <Autocomplete
          disablePortal
          id="superficie"
          value={proposta.superficie || null}
          options={superficies}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          getOptionLabel={(option) => `${option.nome}`}
          key={(option) => option._id}
          sx={styleTextField}
          renderInput={withIcone('superficie', 'Superfície')}
          onChange={(event, newValue) => {
            setProposta((prevProposta) => ({ ...prevProposta, superficie: newValue }));
          }}
        />
      )}

      {tem('orientacao') && (
        <Stack>
          <Autocomplete
            disablePortal
            id="orientacaotelhado"
            value={proposta.orientacaotelhado || null}
            options={orientacoes}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.nome}`}
            sx={styleTextField}
            renderInput={withIcone('orientacao', 'Orientação Telhado')}
            onChange={(event, newValue) => {
              setProposta((prevProposta) => ({ ...prevProposta, orientacaotelhado: newValue }));
            }}
          />
          {proposta.orientacaotelhado?._id === ID_ORIENTACAO_SUL_SUDOESTE_SUDESTE && (
            <FormControlLabel
              control={
                <Checkbox
                  id="rebatimento"
                  onChange={() => setProposta((prev) => ({ ...prev, rebatimento: !prev.rebatimento }))}
                  checked={proposta.rebatimento}
                />
              }
              label="Rebatimento"
            />
          )}
        </Stack>
      )}

      {tem('disjuntor') && (
        <Stack direction="row" gap={2}>
          <Autocomplete
            disablePortal
            id="tipodisjuntor"
            value={proposta.disjuntorcliente?.tipodisjuntor}
            options={tiposDisjuntor}
            getOptionLabel={(option) => `${option.nome}`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={styleTextFieldPequeno}
            size="small"
            renderInput={withIcone('disjuntor', 'Tipo Disjuntor')}
            onChange={(event, newValue) => {
              setProposta((prev) => ({
                ...prev,
                disjuntorcliente: { ...prev.disjuntorcliente, tipodisjuntor: newValue },
              }));
            }}
          />
          <Autocomplete
            disablePortal
            id="potenciaminima"
            value={proposta.disjuntorcliente?.potenciadisjuntor}
            options={potenciasDisjuntor}
            getOptionLabel={(option) => `${option.valor}`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={styleTextFieldPequeno}
            size="small"
            renderInput={(params) => <TextField {...params} label="Potência Mínima" />}
            onChange={(event, newValue) => {
              setProposta((prev) => ({
                ...prev,
                disjuntorcliente: { ...prev.disjuntorcliente, potenciadisjuntor: newValue },
              }));
            }}
          />
        </Stack>
      )}

      {tem('origem') && (
        <Autocomplete
          disablePortal
          id="origem"
          value={proposta.origem || null}
          options={origens}
          isOptionEqualToValue={(option, value) => option.nome === value.nome}
          getOptionLabel={(option) => `${option.nome}`}
          sx={styleTextField}
          renderInput={withIcone('origem', 'Origem')}
          onChange={(event, newValue) => {
            setProposta((prevProposta) => ({ ...prevProposta, origem: newValue }));
          }}
        />
      )}

      {tem('unidade') && (
        <Autocomplete
          disablePortal
          id="unidade"
          value={proposta.unidade || null}
          options={unidades}
          sx={styleTextField}
          isOptionEqualToValue={isSameUnidade}
          getOptionLabel={(option) => `${option.nome}`}
          renderInput={withIcone('unidade', 'Unidade')}
          onChange={(event, newValue) => {
            setProposta((prevProposta) => ({ ...prevProposta, unidade: newValue }));
          }}
        />
      )}
    </Stack>
  );
};

CamposInstalacao.propTypes = {
  campos: PropTypes.arrayOf(PropTypes.oneOf([
    'cliente', 'estado', 'cidade', 'superficie', 'orientacao', 'disjuntor', 'origem', 'unidade',
  ])).isRequired,
  proposta: PropTypes.object.isRequired,
  setProposta: PropTypes.func.isRequired,
  rowsClientes: PropTypes.array,
  estados: PropTypes.array,
  cidades: PropTypes.array,
  superficies: PropTypes.array,
  orientacoes: PropTypes.array,
  tiposDisjuntor: PropTypes.array,
  potenciasDisjuntor: PropTypes.array,
  origens: PropTypes.array,
  unidades: PropTypes.array,
  isSameUnidade: PropTypes.func,
  onAbrirModalCliente: PropTypes.func,
};

CamposInstalacao.defaultProps = {
  rowsClientes: [],
  estados: [],
  cidades: [],
  superficies: [],
  orientacoes: [],
  tiposDisjuntor: [],
  potenciasDisjuntor: [],
  origens: [],
  unidades: [],
  isSameUnidade: (option, value) => option?._id === value?._id,
  onAbrirModalCliente: () => {},
};

export default CamposInstalacao;